import { useEffect, useCallback, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Stack,
  CircularProgress,
  IconButton,
  Chip,
  Divider,
  Pagination,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaPlay,
  FaEye,
  FaWallet,
  FaCalendarAlt,
  FaChartLine,
  FaVideo,
  FaClock,
  FaTrophy,
} from "react-icons/fa";

import { fetchOnlineTeacherStats } from "../utils/generalSubjectApi";

export default function OnlineTeacherStatsPage() {
  const navigate = useNavigate();

  const now = new Date();

  const [selectedDate, setSelectedDate] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
  );

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [videoPage, setVideoPage] = useState(1);

  const videosPerPage = 10;

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);

      const [year, month] = selectedDate.split("-");

      const data = await fetchOnlineTeacherStats(year, month);

      setStats(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    setVideoPage(1);
  }, [selectedDate]);
  
  if (loading) {
    return <LoadingScreen />;
  }

  const teacherName = stats?.teacher?.name || "Teacher";

  const selectedWatches = Number(stats?.selected?.watches || 0);
  const selectedEarnings = Number(
    stats?.selected?.teacher_revenue || 0
  );

  const yearWatches = Number(stats?.year?.watches || 0);
  const yearEarnings = Number(
    stats?.year?.teacher_revenue || 0
  );

  const allTimeWatches = Number(
    stats?.all_time?.watches || 0
  );

  const allTimeEarnings = Number(
    stats?.all_time?.teacher_revenue || 0
  );

  const videos = stats?.videos || [];

  const totalVideoPages = Math.ceil(
    videos.length / videosPerPage
  );

  const paginatedVideos = videos.slice(
    (videoPage - 1) * videosPerPage,
    videoPage * videosPerPage
  );



  const bestVideo =
    [...videos].sort(
      (a, b) =>
        Number(b.selected_month?.watches || 0) -
        Number(a.selected_month?.watches || 0)
    )[0] || null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
        pb: 8,
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          background:
            "linear-gradient(135deg, #0f172a 0%, #172554 55%, #1e3a8a 100%)",
          color: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: "absolute",
            width: 350,
            height: 350,
            borderRadius: "50%",
            background: "rgba(59,130,246,0.14)",
            top: -180,
            right: -100,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            width: 250,
            height: 250,
            borderRadius: "50%",
            background: "rgba(16,185,129,0.10)",
            bottom: -180,
            left: -80,
          }}
        />

        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            px: { xs: 2, md: 5 },
            py: { xs: 2.5, md: 4 },
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* TOP NAV */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={5}
          >
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                width: 42,
                height: 42,
                color: "#fff",
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.12)",
                "&:hover": {
                  background: "rgba(255,255,255,0.18)",
                },
              }}
            >
              <FaArrowLeft size={15} />
            </IconButton>

            <Chip
              icon={<FaChartLine size={13} />}
              label="My Performance"
              sx={{
                color: "#fff",
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.15)",
                fontWeight: 700,
                "& .MuiChip-icon": {
                  color: "#60a5fa",
                },
              }}
            />
          </Stack>

          {/* HERO */}
          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            justifyContent="space-between"
            alignItems={{
              xs: "flex-start",
              md: "flex-end",
            }}
            gap={4}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: "#93c5fd",
                  fontWeight: 800,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  mb: 1,
                }}
              >
                Teacher Dashboard
              </Typography>

              <Typography
                sx={{
                  fontSize: {
                    xs: "2rem",
                    md: "2.8rem",
                  },
                  lineHeight: 1.1,
                  fontWeight: 900,
                  letterSpacing: "-0.04em",
                }}
              >
                Welcome, {teacherName}
              </Typography>

              <Typography
                sx={{
                  mt: 1.5,
                  color: "#cbd5e1",
                  maxWidth: 520,
                  fontSize: "0.95rem",
                }}
              >
                Track your video performance and earnings
                from one place.
              </Typography>
            </Box>

            {/* DATE SELECTOR */}
            <Box
              sx={{
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.14)",
                borderRadius: "16px",
                p: 1,
                backdropFilter: "blur(10px)",
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ px: 1 }}
              >
                <FaCalendarAlt
                  size={14}
                  color="#93c5fd"
                />

                <TextField
                  type="month"
                  value={selectedDate}
                  onChange={(e) =>
                    setSelectedDate(e.target.value)
                  }
                  size="small"
                  sx={{
                    minWidth: 155,
                    "& .MuiOutlinedInput-root": {
                      color: "#fff",
                      borderRadius: "10px",
                      "& fieldset": {
                        borderColor:
                          "rgba(255,255,255,0.15)",
                      },
                      "&:hover fieldset": {
                        borderColor:
                          "rgba(255,255,255,0.3)",
                      },
                    },
                    "& input": {
                      color: "#fff",
                    },
                    "& input::-webkit-calendar-picker-indicator":
                      {
                        filter: "invert(1)",
                      },
                  }}
                />
              </Stack>
            </Box>
          </Stack>

          {/* MAIN EARNINGS CARD */}
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box
              sx={{
                mt: 5,
                p: { xs: 2.5, md: 3.5 },
                borderRadius: "24px",
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.13), rgba(255,255,255,0.06))",
                border:
                  "1px solid rgba(255,255,255,0.14)",
                backdropFilter: "blur(15px)",
              }}
            >
              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                justifyContent="space-between"
                alignItems={{
                  xs: "flex-start",
                  sm: "center",
                }}
                gap={3}
              >
                <Box>
                  <Typography
                    sx={{
                      color: "#bfdbfe",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                    }}
                  >
                    {formatMonth(selectedDate)}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 1,
                      fontSize: {
                        xs: "2.2rem",
                        md: "3rem",
                      },
                      fontWeight: 900,
                      letterSpacing: "-0.04em",
                    }}
                  >
                    ₦{selectedEarnings.toLocaleString()}
                  </Typography>

                  <Typography
                    sx={{
                      color: "#cbd5e1",
                      mt: 0.5,
                      fontSize: "0.85rem",
                    }}
                  >
                    Your 20% earnings this month
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "rgba(16,185,129,0.15)",
                    border:
                      "1px solid rgba(52,211,153,0.2)",
                  }}
                >
                  <FaWallet
                    size={26}
                    color="#34d399"
                  />
                </Box>
              </Stack>
            </Box>
          </motion.div>
        </Box>
      </Box>

      {/* CONTENT */}
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, md: 5 },
          mt: { xs: 3, md: 5 },
        }}
      >
        {/* MONTHLY STATS */}
        <SectionTitle
          eyebrow={formatMonth(selectedDate)}
          title="This Month"
        />

        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={2}
          mb={5}
        >
          <StatCard
            icon={<FaEye size={19} />}
            label="Video Watches"
            value={selectedWatches}
            description="Paid video watches"
            iconBackground="#eff6ff"
            iconColor="#2563eb"
            delay={0}
          />

          <StatCard
            icon={<FaWallet size={19} />}
            label="My Earnings"
            value={selectedEarnings}
            prefix="₦"
            description="Your 20% share"
            iconBackground="#ecfdf5"
            iconColor="#059669"
            delay={0.08}
          />
        </Stack>

        {/* YEARLY PERFORMANCE */}
        <SectionTitle
          eyebrow={String(stats?.year?.year || now.getFullYear())}
          title="Yearly Performance"
        />

        <Box
          sx={{
            background: "#fff",
            borderRadius: "24px",
            border: "1px solid #e2e8f0",
            p: { xs: 2.5, md: 3 },
            mb: 5,
            boxShadow:
              "0 10px 40px rgba(15,23,42,0.04)",
          }}
        >
          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            spacing={3}
          >
            <PerformanceItem
              icon={<FaEye size={18} />}
              label="Year Watches"
              value={yearWatches.toLocaleString()}
            />

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: {
                  xs: "none",
                  md: "block",
                },
              }}
            />

            <PerformanceItem
              icon={<FaWallet size={18} />}
              label="My Year Earnings"
              value={`₦${yearEarnings.toLocaleString()}`}
              highlight
            />

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: {
                  xs: "none",
                  md: "block",
                },
              }}
            />

            <PerformanceItem
              icon={<FaVideo size={18} />}
              label="My Videos"
              value={videos.length.toLocaleString()}
            />
          </Stack>
        </Box>

        {/* ALL TIME */}
        <SectionTitle
          eyebrow="Lifetime"
          title="Your Overall Performance"
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr 1fr",
            },
            gap: 2,
            mb: 5,
          }}
        >
          <BigStatCard
            icon={<FaEye />}
            title="Total Watches"
            value={allTimeWatches}
            description="Total paid watches across your videos"
          />

          <BigStatCard
            icon={<FaWallet />}
            title="Total My Earnings"
            value={allTimeEarnings}
            prefix="₦"
            description="Your accumulated 20% earnings"
            green
          />
        </Box>

        {/* TOP VIDEO */}
        {bestVideo && (
          <>
            <SectionTitle
              eyebrow="Top Performer"
              title="Your Best Video"
            />

            <motion.div
              initial={{
                opacity: 0,
                y: 15,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
            >
              <Box
                sx={{
                  mb: 5,
                  borderRadius: "24px",
                  overflow: "hidden",
                  position: "relative",
                  background:
                    "linear-gradient(135deg, #111827, #1e293b)",
                  color: "#fff",
                  p: { xs: 2.5, md: 4 },
                  boxShadow:
                    "0 20px 50px rgba(15,23,42,0.14)",
                }}
              >
                <Stack
                  direction={{
                    xs: "column",
                    sm: "row",
                  }}
                  justifyContent="space-between"
                  gap={3}
                >
                  <Box>
                    <Chip
                      icon={<FaTrophy size={12} />}
                      label="Top Video"
                      size="small"
                      sx={{
                        mb: 2,
                        color: "#fde68a",
                        background:
                          "rgba(250,204,21,0.12)",
                        border:
                          "1px solid rgba(250,204,21,0.18)",
                        fontWeight: 700,
                        "& .MuiChip-icon": {
                          color: "#facc15",
                        },
                      }}
                    />

                    <Typography
                      sx={{
                        fontSize: {
                          xs: "1.3rem",
                          md: "1.7rem",
                        },
                        fontWeight: 900,
                      }}
                    >
                      {bestVideo.title}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.7,
                        color: "#94a3b8",
                        fontSize: "0.85rem",
                      }}
                    >
                      {bestVideo.category ||
                        "No category"}
                      {bestVideo.year
                        ? ` • ${bestVideo.year}`
                        : ""}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      minWidth: {
                        sm: 180,
                      },
                      p: 2,
                      borderRadius: "16px",
                      background:
                        "rgba(255,255,255,0.06)",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#94a3b8",
                        fontSize: "0.7rem",
                        textTransform: "uppercase",
                        fontWeight: 700,
                      }}
                    >
                      Selected Month
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.5,
                        fontSize: "1.7rem",
                        fontWeight: 900,
                      }}
                    >
                      {Number(
                        bestVideo.selected_month
                          ?.watches || 0
                      ).toLocaleString()}
                    </Typography>

                    <Typography
                      sx={{
                        color: "#94a3b8",
                        fontSize: "0.75rem",
                      }}
                    >
                      watches
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </motion.div>
          </>
        )}

        {/* VIDEOS */}
        <SectionTitle
          eyebrow="Content"
          title={`My Videos (${videos.length})`}
        />

        <Stack spacing={2} id="my-videos-section">
          {videos.length === 0 ? (
            <EmptyVideos />
          ) : (
            paginatedVideos.map((video, index) => (
              <VideoCard
                key={video.id}
                video={video}
                index={index}
              />
            ))
          )}
        </Stack>

        {/* VIDEO PAGINATION */}
        {totalVideoPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 4,
              mb: 3,
              p: 2,
              background: "#fff",
              borderRadius: "16px",
              border: "1px solid #e2e8f0",
            }}
          >
            <Pagination
              count={totalVideoPages}
              page={videoPage}
              onChange={(event, value) => {
                setVideoPage(value);

                // Bring the video list into view
                document
                  .getElementById("my-videos-section")
                  ?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
              }}
              color="primary"
              shape="rounded"
              siblingCount={1}
              boundaryCount={1}
              size="large"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

/* =========================================================
   COMPONENTS
========================================================= */

function SectionTitle({ eyebrow, title }) {
  return (
    <Box mb={2.5}>
      <Typography
        sx={{
          color: "#64748b",
          fontSize: "0.68rem",
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: "0.12em",
        }}
      >
        {eyebrow}
      </Typography>

      <Typography
        sx={{
          mt: 0.5,
          fontSize: {
            xs: "1.35rem",
            md: "1.55rem",
          },
          fontWeight: 900,
          color: "#0f172a",
          letterSpacing: "-0.025em",
        }}
      >
        {title}
      </Typography>
    </Box>
  );
}

function StatCard({
  icon,
  label,
  value,
  prefix = "",
  description,
  iconBackground,
  iconColor,
  delay,
}) {
  return (
    <motion.div
      initial={{
        opacity: 0,
        y: 15,
      }}
      animate={{
        opacity: 1,
        y: 0,
      }}
      transition={{
        duration: 0.4,
        delay,
      }}
      style={{
        flex: 1,
      }}
    >
      <Box
        sx={{
          background: "#fff",
          borderRadius: "22px",
          border: "1px solid #e2e8f0",
          p: 3,
          height: "100%",
          boxShadow:
            "0 10px 35px rgba(15,23,42,0.04)",
          transition: "all 0.2s ease",
          "&:hover": {
            transform: "translateY(-3px)",
            boxShadow:
              "0 15px 40px rgba(15,23,42,0.08)",
          },
        }}
      >
        <Box
          sx={{
            width: 45,
            height: 45,
            borderRadius: "13px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: iconBackground,
            color: iconColor,
            mb: 2.5,
          }}
        >
          {icon}
        </Box>

        <Typography
          sx={{
            color: "#64748b",
            fontSize: "0.76rem",
            fontWeight: 700,
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            mt: 0.8,
            fontSize: "1.9rem",
            fontWeight: 900,
            color: "#0f172a",
            letterSpacing: "-0.035em",
          }}
        >
          {prefix}
          {Number(value || 0).toLocaleString()}
        </Typography>

        <Typography
          sx={{
            mt: 0.5,
            color: "#94a3b8",
            fontSize: "0.78rem",
          }}
        >
          {description}
        </Typography>
      </Box>
    </motion.div>
  );
}

function PerformanceItem({
  icon,
  label,
  value,
  highlight = false,
}) {
  return (
    <Box sx={{ flex: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.2}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            background: highlight
              ? "#ecfdf5"
              : "#f1f5f9",
            color: highlight
              ? "#059669"
              : "#475569",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>

        <Typography
          sx={{
            fontSize: "0.75rem",
            color: "#64748b",
            fontWeight: 700,
          }}
        >
          {label}
        </Typography>
      </Stack>

      <Typography
        sx={{
          mt: 1.5,
          fontSize: "1.5rem",
          fontWeight: 900,
          color: highlight
            ? "#059669"
            : "#0f172a",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function BigStatCard({
  icon,
  title,
  value,
  prefix = "",
  description,
  green = false,
}) {
  return (
    <Box
      sx={{
        background: green
          ? "linear-gradient(135deg,#ecfdf5,#f0fdf4)"
          : "#fff",
        borderRadius: "24px",
        border: green
          ? "1px solid #bbf7d0"
          : "1px solid #e2e8f0",
        p: { xs: 2.5, md: 3.5 },
        boxShadow:
          "0 10px 40px rgba(15,23,42,0.04)",
      }}
    >
      <Box
        sx={{
          width: 46,
          height: 46,
          borderRadius: "13px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: green
            ? "#dcfce7"
            : "#f1f5f9",
          color: green
            ? "#059669"
            : "#475569",
        }}
      >
        {icon}
      </Box>

      <Typography
        sx={{
          mt: 2.5,
          color: "#64748b",
          fontSize: "0.75rem",
          fontWeight: 700,
          textTransform: "uppercase",
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          mt: 0.7,
          fontSize: {
            xs: "1.8rem",
            md: "2.2rem",
          },
          fontWeight: 900,
          color: green
            ? "#047857"
            : "#0f172a",
          letterSpacing: "-0.04em",
        }}
      >
        {prefix}
        {Number(value || 0).toLocaleString()}
      </Typography>

      <Typography
        sx={{
          mt: 0.5,
          color: "#94a3b8",
          fontSize: "0.8rem",
        }}
      >
        {description}
      </Typography>
    </Box>
  );
}

function VideoCard({ video, index }) {
  const monthWatches = Number(
    video.selected_month?.watches || 0
  );

  const totalWatches = Number(
    video.total_watch_count || 0
  );

  return (
    <motion.div
      initial={{
        opacity: 0,
        y: 10,
      }}
      animate={{
        opacity: 1,
        y: 0,
      }}
      transition={{
        duration: 0.35,
        delay: index * 0.04,
      }}
    >
      <Box
        sx={{
          background: "#fff",
          borderRadius: "20px",
          border: "1px solid #e2e8f0",
          p: { xs: 2, md: 2.5 },
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: "#bfdbfe",
            boxShadow:
              "0 10px 35px rgba(37,99,235,0.07)",
          },
        }}
      >
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          alignItems={{
            xs: "flex-start",
            md: "center",
          }}
          justifyContent="space-between"
          gap={2}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{
              minWidth: 0,
              flex: 1,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                minWidth: 48,
                borderRadius: "14px",
                background:
                  "linear-gradient(135deg,#eff6ff,#dbeafe)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#2563eb",
              }}
            >
              <FaPlay size={16} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  color: "#0f172a",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: {
                    xs: "260px",
                    md: "450px",
                  },
                }}
              >
                {video.title}
              </Typography>

              <Typography
                sx={{
                  mt: 0.4,
                  fontSize: "0.75rem",
                  color: "#94a3b8",
                }}
              >
                {video.category ||
                  "No category"}
                {video.year
                  ? ` • ${video.year}`
                  : ""}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={3}
            sx={{
              width: {
                xs: "100%",
                md: "auto",
              },
            }}
          >
            <VideoMetric
              icon={<FaCalendarAlt size={11} />}
              label="This Month"
              value={monthWatches}
            />

            <VideoMetric
              icon={<FaClock size={11} />}
              label="All Time"
              value={totalWatches}
            />
          </Stack>
        </Stack>
      </Box>
    </motion.div>
  );
}

function VideoMetric({ icon, label, value }) {
  return (
    <Box>
      <Stack
        direction="row"
        spacing={0.7}
        alignItems="center"
      >
        <Box
          sx={{
            color: "#94a3b8",
            display: "flex",
          }}
        >
          {icon}
        </Box>

        <Typography
          sx={{
            color: "#94a3b8",
            fontSize: "0.65rem",
            fontWeight: 700,
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>
      </Stack>

      <Typography
        sx={{
          mt: 0.5,
          fontWeight: 900,
          color: "#0f172a",
          fontSize: "1.05rem",
        }}
      >
        {Number(value || 0).toLocaleString()}
      </Typography>
    </Box>
  );
}

function EmptyVideos() {
  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "22px",
        border: "1px dashed #cbd5e1",
        p: 5,
        textAlign: "center",
      }}
    >
      <FaVideo
        size={30}
        color="#94a3b8"
      />

      <Typography
        sx={{
          mt: 2,
          fontWeight: 800,
          color: "#334155",
        }}
      >
        No videos yet
      </Typography>

      <Typography
        sx={{
          mt: 0.5,
          color: "#94a3b8",
          fontSize: "0.85rem",
        }}
      >
        Your uploaded videos will appear here.
      </Typography>
    </Box>
  );
}

function LoadingScreen() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(180deg,#f8fafc,#eef2ff)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Stack
        alignItems="center"
        spacing={2}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "20px",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow:
              "0 15px 40px rgba(15,23,42,0.08)",
          }}
        >
          <CircularProgress
            size={28}
            thickness={4}
          />
        </Box>

        <Typography
          sx={{
            color: "#64748b",
            fontSize: "0.85rem",
            fontWeight: 600,
          }}
        >
          Loading your dashboard...
        </Typography>
      </Stack>
    </Box>
  );
}

function formatMonth(value) {
  if (!value) return "";

  const [year, month] = value.split("-");

  const date = new Date(
    Number(year),
    Number(month) - 1,
    1
  );

  return date.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}